import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { TrackReference } from '@livekit/components-react'
import type { Participant } from 'livekit-client'

import { getGridLayout, paginate } from './gridLayout'
import { GridPage } from './GridPage'
import { getMobileGridLayout } from './mobileGridLayout'
import { MobileGridPage } from './MobileGridPage'
import { useIsMobileViewport } from './useIsMobileViewport'
import { useSpeakerPromotion } from './useSpeakerPromotion'
import styles from './medentee.module.css'

export type GridProps = {
  participants: Participant[]
  trackByIdentity: Record<string, TrackReference>
}

export function Grid({ participants, trackByIdentity }: GridProps) {
  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  const [size, setSize] = useState({ width: 0, height: 0 })

  const stageRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const stage = stageRef.current

    if (!stage) {
      return
    }

    let frame = 0

    const measure = () => {
      const width = stage.clientWidth
      const height = stage.clientHeight

      setSize((current) =>
        current.width === width && current.height === height ? current : { width, height }
      )
    }

    measure()

    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    })

    observer.observe(stage)

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [])

  const isMobile = useIsMobileViewport()

  const pages = useMemo(
    () => (isMobile ? [participants] : paginate(participants)),
    [participants, isMobile]
  )

  const current = Math.min(page, pages.length - 1)

  useEffect(() => {
    if (page !== current) {
      setPage(current)
    }
  }, [page, current])

  const currentPage = pages[current] ?? []

  const layout = useMemo(
    () => getGridLayout(currentPage.length, size.width, size.height),
    [currentPage.length, size.width, size.height]
  )

  const mobileLayout = useMemo(
    () => getMobileGridLayout(currentPage.length, size.height),
    [currentPage.length, size.height]
  )

  const promotedPage = useSpeakerPromotion(
    currentPage,
    isMobile && mobileLayout.overflow > 0 ? mobileLayout.visible : 0
  )

  return (
    <>
      <div className={styles.participants} ref={stageRef}>
        {isMobile ? (
          <MobileGridPage
            participants={promotedPage}
            trackByIdentity={trackByIdentity}
            layout={mobileLayout}
          />
        ) : (
          <GridPage participants={currentPage} trackByIdentity={trackByIdentity} layout={layout} />
        )}
      </div>
      {pages.length > 1 && (
        <div className={styles.pager}>
          {pages.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={t('chat.call.grid.pageButton', { number: index + 1 })}
              className={`${styles.pagerDot} ${index === current ? styles.pagerDotActive : ''}`}
              onClick={() => setPage(index)}
            />
          ))}
        </div>
      )}
    </>
  )
}
